import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  BarChart3, Users, Clock, MousePointer, TrendingUp, 
  Globe, Monitor, ArrowUpRight, ArrowDownRight, RefreshCw,
  Eye, Target, Zap, Brain, ChevronRight, ExternalLink,
  Smartphone, Laptop, Activity, UserCheck, UserX
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [activeVisitors, setActiveVisitors] = useState([]);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    try {
      const [analyticsRes, activeRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/summary?period=${selectedPeriod}`),
        axios.get(`${API_URL}/api/analytics/active`)
      ]);
      setAnalytics(analyticsRes.data);
      setActiveVisitors(activeRes.data.active_visitors || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to fetch analytics');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAiInsights = async () => {
    setIsLoadingAI(true);
    try {
      const response = await axios.get(`${API_URL}/api/analytics/ai-insights`);
      setAiInsights(response.data);
    } catch (error) {
      console.error('Error fetching AI insights:', error);
      toast.error('Failed to fetch AI insights');
    } finally {
      setIsLoadingAI(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Refresh active visitors every 30 seconds
    const interval = setInterval(() => {
      axios.get(`${API_URL}/api/analytics/active`)
        .then(res => setActiveVisitors(res.data.active_visitors || []))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedPeriod]);

  const StatCard = ({ icon: Icon, label, value, subValue, trend, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#111] border border-white/10 rounded-xl p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg`} style={{ backgroundColor: `${color}20` }}>
          <Icon size={20} style={{ color }} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-white/50">{label}</p>
      {subValue && <p className="text-xs text-white/40 mt-1">{subValue}</p>}
    </motion.div>
  );

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  const data = analytics || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="text-[#D4AF37]" />
            Traffic Analytics
          </h2>
          <p className="text-sm text-white/50">Real-time visitor insights & AI predictions</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
          
          <button
            onClick={fetchAnalytics}
            className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 hover:text-white transition-colors"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Live Active Visitors */}
      {activeVisitors.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-green-400 font-semibold">{activeVisitors.length} Active Visitor{activeVisitors.length > 1 ? 's' : ''} Right Now</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {activeVisitors.slice(0, 5).map((visitor, idx) => (
              <div key={idx} className="bg-black/30 rounded-lg px-3 py-2 text-sm">
                <span className="text-white/70">{visitor.page_url}</span>
                <span className="text-white/40 ml-2">({formatDuration(visitor.time_spent)})</span>
              </div>
            ))}
            {activeVisitors.length > 5 && (
              <div className="bg-black/30 rounded-lg px-3 py-2 text-sm text-white/40">
                +{activeVisitors.length - 5} more
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Visitors"
          value={data.total_visitors || 0}
          subValue={`${data.unique_visitors || 0} unique`}
          trend={data.visitor_trend}
          color="#D4AF37"
        />
        <StatCard
          icon={Eye}
          label="Page Views"
          value={data.total_pageviews || 0}
          subValue={`${(data.pages_per_session || 0).toFixed(1)} per session`}
          trend={data.pageview_trend}
          color="#50C878"
        />
        <StatCard
          icon={Clock}
          label="Avg. Session Duration"
          value={formatDuration(data.avg_session_duration || 0)}
          subValue={`${data.total_sessions || 0} sessions`}
          color="#007AFF"
        />
        <StatCard
          icon={MousePointer}
          label="Avg. Scroll Depth"
          value={`${data.avg_scroll_depth || 0}%`}
          subValue="of page scrolled"
          color="#E4405F"
        />
      </div>

      {/* Traffic Sources & Top Pages */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Traffic Sources */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Globe size={18} className="text-[#D4AF37]" />
            Traffic Sources
          </h3>
          <div className="space-y-3">
            {(data.traffic_sources || []).slice(0, 6).map((source, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    source.source === 'direct' ? 'bg-blue-500/20' :
                    source.source.includes('google') ? 'bg-red-500/20' :
                    source.source.includes('linkedin') ? 'bg-blue-600/20' :
                    source.source.includes('facebook') ? 'bg-blue-500/20' :
                    source.source.includes('twitter') ? 'bg-sky-500/20' :
                    'bg-white/10'
                  }`}>
                    <ExternalLink size={14} className="text-white/60" />
                  </div>
                  <div>
                    <p className="text-white text-sm">{source.source}</p>
                    <p className="text-white/40 text-xs">{source.visits} visits</p>
                  </div>
                </div>
                <div className="flex-1 mx-4 h-2 bg-white/5 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#D4AF37] rounded-full"
                    style={{ width: `${(source.visits / (data.total_visitors || 1)) * 100}%` }}
                  />
                </div>
                <span className="text-white/60 text-sm">
                  {((source.visits / (data.total_visitors || 1)) * 100).toFixed(1)}%
                </span>
              </div>
            ))}
            {(!data.traffic_sources || data.traffic_sources.length === 0) && (
              <p className="text-white/40 text-center py-4">No traffic data yet</p>
            )}
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Activity size={18} className="text-[#50C878]" />
            Top Pages
          </h3>
          <div className="space-y-3">
            {(data.top_pages || []).slice(0, 6).map((page, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-[#D4AF37] text-black' :
                    idx === 1 ? 'bg-white/20 text-white' :
                    idx === 2 ? 'bg-amber-700/50 text-amber-200' :
                    'bg-white/5 text-white/40'
                  }`}>
                    {idx + 1}
                  </span>
                  <div>
                    <p className="text-white text-sm">{page.page_url || '/'}</p>
                    <p className="text-white/40 text-xs">Avg. {formatDuration(page.avg_time || 0)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-semibold">{page.views}</p>
                  <p className="text-white/40 text-xs">views</p>
                </div>
              </div>
            ))}
            {(!data.top_pages || data.top_pages.length === 0) && (
              <p className="text-white/40 text-center py-4">No page data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Device & Engagement Breakdown */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Device Types */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Monitor size={18} className="text-[#007AFF]" />
            Devices
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Laptop size={18} className="text-white/60" />
                <span className="text-white">Desktop</span>
              </div>
              <span className="text-[#D4AF37] font-semibold">{data.devices?.desktop || 0}%</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Smartphone size={18} className="text-white/60" />
                <span className="text-white">Mobile</span>
              </div>
              <span className="text-[#D4AF37] font-semibold">{data.devices?.mobile || 0}%</span>
            </div>
            <div className="h-3 bg-white/5 rounded-full overflow-hidden flex">
              <div 
                className="h-full bg-[#007AFF]" 
                style={{ width: `${data.devices?.desktop || 50}%` }}
              />
              <div 
                className="h-full bg-[#50C878]" 
                style={{ width: `${data.devices?.mobile || 50}%` }}
              />
            </div>
          </div>
        </div>

        {/* Visitor Intent Signals */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Target size={18} className="text-[#E4405F]" />
            Engagement Signals
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-white/70">Clicked Packages</span>
              <span className="text-[#50C878] font-semibold">{data.engagement?.package_clicks || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/70">Started Forms</span>
              <span className="text-[#D4AF37] font-semibold">{data.engagement?.form_starts || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/70">Voice Agent Opens</span>
              <span className="text-[#007AFF] font-semibold">{data.engagement?.voice_opens || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/70">WhatsApp Clicks</span>
              <span className="text-green-500 font-semibold">{data.engagement?.whatsapp_clicks || 0}</span>
            </div>
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-[#D4AF37]" />
            Conversion Funnel
          </h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-white/70 text-sm">Visitors</span>
                <span className="text-white font-semibold">{data.funnel?.visitors || 0}</span>
              </div>
              <div className="h-2 bg-white/5 rounded-full">
                <div className="h-full bg-[#D4AF37] rounded-full w-full" />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-white/70 text-sm">Engaged (&gt;30s)</span>
                <span className="text-white font-semibold">{data.funnel?.engaged || 0}</span>
              </div>
              <div className="h-2 bg-white/5 rounded-full">
                <div className="h-full bg-[#50C878] rounded-full" style={{ width: `${(data.funnel?.engaged / (data.funnel?.visitors || 1)) * 100}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-white/70 text-sm">Form Submissions</span>
                <span className="text-white font-semibold">{data.funnel?.submissions || 0}</span>
              </div>
              <div className="h-2 bg-white/5 rounded-full">
                <div className="h-full bg-[#007AFF] rounded-full" style={{ width: `${(data.funnel?.submissions / (data.funnel?.visitors || 1)) * 100}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-white/70 text-sm">Payments</span>
                <span className="text-white font-semibold">{data.funnel?.payments || 0}</span>
              </div>
              <div className="h-2 bg-white/5 rounded-full">
                <div className="h-full bg-[#E4405F] rounded-full" style={{ width: `${(data.funnel?.payments / (data.funnel?.visitors || 1)) * 100}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights Section */}
      <div className="bg-gradient-to-br from-[#1a1a1a] to-[#111] border border-[#D4AF37]/30 rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#D4AF37]/20">
              <Brain size={24} className="text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">AI Behavior Analysis</h3>
              <p className="text-white/50 text-sm">Powered by advanced analytics</p>
            </div>
          </div>
          <button
            onClick={fetchAiInsights}
            disabled={isLoadingAI}
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/20 text-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/30 transition-colors disabled:opacity-50"
          >
            {isLoadingAI ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Zap size={16} />
            )}
            {aiInsights ? 'Refresh Insights' : 'Generate Insights'}
          </button>
        </div>

        {aiInsights ? (
          <div className="space-y-4">
            {/* Prediction Cards */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-black/30 rounded-lg p-4 border border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <UserCheck size={18} className="text-green-400" />
                  <span className="text-white font-semibold">Return Probability</span>
                </div>
                <p className="text-3xl font-bold text-green-400">{aiInsights.return_probability || 0}%</p>
                <p className="text-white/50 text-sm mt-1">{aiInsights.return_reason || 'Based on engagement patterns'}</p>
              </div>
              
              <div className="bg-black/30 rounded-lg p-4 border border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <Target size={18} className="text-[#D4AF37]" />
                  <span className="text-white font-semibold">Purchase Intent</span>
                </div>
                <p className="text-3xl font-bold text-[#D4AF37]">{aiInsights.purchase_probability || 0}%</p>
                <p className="text-white/50 text-sm mt-1">{aiInsights.purchase_reason || 'Based on behavior signals'}</p>
              </div>
            </div>

            {/* AI Summary */}
            <div className="bg-black/30 rounded-lg p-4 border border-white/5">
              <h4 className="text-white font-semibold mb-2">Key Insights</h4>
              <ul className="space-y-2">
                {(aiInsights.insights || []).map((insight, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-white/70">
                    <ChevronRight size={16} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span>{insight}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Recommendations */}
            {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
              <div className="bg-[#D4AF37]/10 rounded-lg p-4 border border-[#D4AF37]/20">
                <h4 className="text-[#D4AF37] font-semibold mb-2">Recommendations</h4>
                <ul className="space-y-2">
                  {aiInsights.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-white/80">
                      <Zap size={16} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Brain size={48} className="text-white/20 mx-auto mb-3" />
            <p className="text-white/40">Click "Generate Insights" to get AI-powered analysis</p>
            <p className="text-white/30 text-sm mt-1">Analyzes visitor behavior to predict return & purchase likelihood</p>
          </div>
        )}
      </div>

      {/* Location & Browser Stats */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Countries */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Globe size={18} className="text-[#50C878]" />
            Top Countries
          </h3>
          <div className="space-y-3">
            {(data.top_countries || []).slice(0, 6).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-[#D4AF37] text-black' :
                    idx === 1 ? 'bg-white/20 text-white' :
                    'bg-white/5 text-white/40'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="text-white">{item.country}</span>
                </div>
                <span className="text-white/60">{item.visits} visits</span>
              </div>
            ))}
            {(!data.top_countries || data.top_countries.length === 0) && (
              <p className="text-white/40 text-center py-4">No location data yet</p>
            )}
          </div>
        </div>

        {/* Browsers */}
        <div className="bg-[#111] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Monitor size={18} className="text-[#007AFF]" />
            Browsers
          </h3>
          <div className="space-y-3">
            {Object.entries(data.browsers || {}).slice(0, 5).map(([browser, percentage], idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-white">{browser}</span>
                <div className="flex items-center gap-3">
                  <div className="w-24 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#007AFF] rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-white/60 w-12 text-right">{percentage}%</span>
                </div>
              </div>
            ))}
            {(!data.browsers || Object.keys(data.browsers).length === 0) && (
              <p className="text-white/40 text-center py-4">No browser data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Visitors Table */}
      <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Users size={18} className="text-[#D4AF37]" />
            Recent Visitors
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Visitor ID</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Date & Time</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Location</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Device</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Source</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Pages</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Duration</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Intent</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_visitors || []).slice(0, 10).map((visitor, idx) => {
                const visitDate = visitor.timestamp ? new Date(visitor.timestamp) : null;
                return (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <p className="text-white text-sm font-mono">{visitor.visitor_id?.substring(0, 14)}...</p>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white text-sm">
                          {visitDate ? visitDate.toLocaleDateString('en-IN', { 
                            day: 'numeric', 
                            month: 'short', 
                            year: 'numeric' 
                          }) : 'N/A'}
                        </p>
                        <p className="text-[#D4AF37] text-xs font-medium">
                          {visitDate ? visitDate.toLocaleTimeString('en-IN', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true
                          }) : ''}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white text-sm">
                          {visitor.city && visitor.country ? `${visitor.city}, ${visitor.country}` : visitor.country || 'Unknown'}
                        </p>
                        {visitor.region && (
                          <p className="text-white/40 text-xs">{visitor.region}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white text-sm">{visitor.browser || 'Unknown'}</p>
                        <p className="text-white/40 text-xs">{visitor.os || ''} • {visitor.device_type || ''}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/70 text-sm">{visitor.referrer_host || 'direct'}</td>
                    <td className="px-4 py-3 text-white/70 text-sm">{visitor.pages_viewed || 1}</td>
                    <td className="px-4 py-3 text-white/70 text-sm">{formatDuration(visitor.time_spent || 0)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        visitor.intent_score >= 70 ? 'bg-green-500/20 text-green-400' :
                        visitor.intent_score >= 40 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-white/10 text-white/40'
                      }`}>
                        {visitor.intent_score >= 70 ? 'High' : visitor.intent_score >= 40 ? 'Medium' : 'Low'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {(!data.recent_visitors || data.recent_visitors.length === 0) && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-white/40">
                    No visitor data yet. Analytics will appear as visitors browse the site.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
